import React, { useMemo, useState } from 'react';
import { EFFECT_ICONS, SUGGEST_ICON } from '../editor/inspector/effectIcons';
import { getAuth } from 'firebase/auth';
import PreviewCanvas from './PreviewCanvas';
import { SuggestVideoWorkspaceBlocks } from '../../../Client/ManagementServiceClient';
import { useWallet } from '../../../hooks/useWallet';
import './effectBlocks.css';

// Icon + label per block type, matched to the Effects dropdown so a suggestion
// reads like the block it will open.
const TYPE_META = {
    text: { icon: EFFECT_ICONS.text, label: 'Text' },
    box: { icon: EFFECT_ICONS.box, label: 'Box' },
    crop: { icon: EFFECT_ICONS.crop, label: 'Crop' },
    zoom: { icon: EFFECT_ICONS.zoom, label: 'Zoom' },
    fade: { icon: EFFECT_ICONS.fade, label: 'Fade' },
    flip: { icon: EFFECT_ICONS.flip, label: 'Flip' },
    look: { icon: EFFECT_ICONS.look, label: 'Color' },
};

/**
 * SuggestPanel — the AI "Suggest effects" step (Effect Blocks phase 4). Measures
 * the clip, asks the planner (Opus) for a short list of effect blocks, and shows
 * each with its rationale. Picking one hands the suggestion up so the parent can
 * open the matching editor pre-seeded with `aiSuggestion` — the user still tunes
 * and approves through the normal apply path; nothing is applied here.
 *
 * Props:
 *   videoWorkspaceId - target workspace
 *   frameUrl         - preview URL of the source clip
 *   sourceFileName   - the asset FileName suggestions apply to
 *   onPick           - (suggestion) => void; open the editor seeded with it
 *   onClose          - () => void
 */
function SuggestPanel({ videoWorkspaceId, frameUrl, sourceFileName, onPick, onClose }) {
    const [measured, setMeasured] = useState(null);
    const [goal, setGoal] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [suggestions, setSuggestions] = useState(null); // null = not asked yet
    const { confirmCost } = useWallet();

    const aspectRatio = useMemo(() => {
        const w = Number(measured?.width) || 9;
        const h = Number(measured?.height) || 16;
        return w / h;
    }, [measured]);

    const ready = Number(measured?.width) > 0 && Number(measured?.height) > 0;

    const handleSuggest = async () => {
        if (!ready) {
            setError('Give the clip a moment to load, then try again.');
            return;
        }
        // Quoted before the call. One planner turn is usually cents, so this
        // normally shows nothing at all — it asks only when the server says the
        // price is worth interrupting for, or when the balance cannot cover it.
        const proceed = await confirmCost({
            flow: 'video_edit',
            params: { prompt_text: goal },
            note: 'Asks the planner for a few effects tuned to this clip.',
        });
        if (!proceed) return;

        setLoading(true);
        setError('');
        try {
            const idToken = await getAuth().currentUser.getIdToken();
            const media = {
                width: Math.round(Number(measured.width)),
                height: Math.round(Number(measured.height)),
                duration: Number(measured.duration) || 0,
            };
            const res = await SuggestVideoWorkspaceBlocks(idToken, videoWorkspaceId, sourceFileName, media, goal);
            if (res.status === 200) {
                setSuggestions(res.myJson.Blocks || []);
            } else {
                setError(res.myJson?.detail || 'Could not generate suggestions.');
            }
        } catch (err) {
            setError('Error getting suggestions: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="eb-editor">
            <div className="eb-editor-grid">
                <PreviewCanvas
                    frameUrl={frameUrl}
                    isVideo
                    aspectRatio={aspectRatio}
                    blocks={[]}
                    onMeta={setMeasured}
                    onSelect={() => {}}
                    onChange={() => {}}
                />
                <div className="eb-controls">
                    <h5>Suggest effects</h5>
                    <p className="eb-hint">Describe the vibe you want (optional) and let AI propose a few effects tuned to this clip. Each one opens an editor you can tweak before adding.</p>

                    <label className="eb-ctl">
                        <span>Goal</span>
                        <input
                            className="eb-input"
                            placeholder="e.g. punchy hook, keep it clean…"
                            value={goal}
                            maxLength={160}
                            onChange={(e) => setGoal(e.target.value)}
                        />
                    </label>

                    <div className="eb-actions">
                        <button
                            type="button"
                            className="eb-btn primary"
                            disabled={!ready || loading}
                            onClick={handleSuggest}
                        >
                            {loading ? 'Thinking…' : (suggestions ? 'Suggest again' : 'Suggest effects')}
                        </button>
                    </div>

                    {error && <p className="eb-error">{error}</p>}

                    {suggestions && suggestions.length === 0 && !loading && (
                        <p className="eb-hint">No suggestions this time — try describing a goal above.</p>
                    )}

                    {suggestions && suggestions.length > 0 && (
                        <ul className="eb-suggest-list">
                            {suggestions.map((sugg) => {
                                const meta = TYPE_META[sugg.type] || { icon: SUGGEST_ICON, label: sugg.type };
                                return (
                                    <li key={sugg.id} className="eb-suggest-item">
                                        <div className="eb-suggest-head">
                                            <span className="eb-suggest-icon"><meta.icon size={16} aria-hidden="true" /></span>
                                            <span className="eb-suggest-type">{meta.label}</span>
                                        </div>
                                        {sugg.ai?.rationale && <p className="eb-suggest-why">{sugg.ai.rationale}</p>}
                                        <button
                                            type="button"
                                            className="eb-btn primary eb-suggest-open"
                                            onClick={() => onPick?.(sugg)}
                                        >
                                            Customize &amp; add
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}

                    <div className="eb-actions">
                        {onClose && <button type="button" className="eb-btn" onClick={onClose}>Close</button>}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default SuggestPanel;
